// Generado desde design-system/components/overlays/primitives.tsx — no editar; edita el .ts/.tsx y corre build-ds.
import { createContext, forwardRef, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, } from 'react';
import { createPortal } from 'react-dom';
import { useDbTheme } from '../../theme/ThemeProvider';
import { cn } from '../../lib/cn';
/*
 * Primitivas de capas flotantes (sin dependencias):
 *  Portal · usePresence · useDismiss · useFocusTrap · useInertOthers · useScrollLock · getActiveElement
 *  FloatingLayer (posición anclada: en línea o en portal con flip/clamp) · motionStyle
 * Todas las superposiciones del sistema (Modal, Drawer, Popover, Menu, Tooltip,
 * Select, DateRangePicker…) se construyen con estas piezas.
 */
const FOCUSABLE = 'a[href],area[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"]),[contenteditable="true"]';
/** Elementos enfocables y visibles dentro de `root` (excluye lo que está bajo `[inert]`). */
export function getFocusable(root) {
    if (!root)
        return [];
    return [...root.querySelectorAll(FOCUSABLE)].filter((el) => !el.closest('[inert]') && el.getClientRects().length > 0);
}
/**
 * Elemento con el foco visto desde la raíz de `node` (documento o shadow root).
 * Dentro de un Shadow DOM `document.activeElement` solo ve el host; esto ve el elemento real.
 */
export function getActiveElement(node) {
    const root = node?.getRootNode?.();
    return root?.activeElement ?? (typeof document !== 'undefined' ? document.activeElement : null);
}
/** Documento + shadow roots (o documentos de iframe) donde viven los nodos: ahí se escuchan los eventos de una capa. */
function eventRoots(nodes) {
    const roots = new Set([document]);
    for (const n of nodes) {
        const r = n?.getRootNode?.();
        // nodeType en vez de instanceof: un shadow root de un iframe es de otro `window`
        if (r && (r.nodeType === 9 || (r.nodeType === 11 && 'host' in r)))
            roots.add(r);
    }
    return [...roots];
}
/** Combina refs (objeto o función) en un callback ref. */
export function mergeRefs(...refs) {
    return (node) => {
        for (const r of refs) {
            if (typeof r === 'function')
                r(node);
            else if (r)
                r.current = node;
        }
    };
}
/**
 * Estado controlado/no controlado con callback (patrón open/defaultOpen/onOpenChange).
 * Controlado cuando `value !== undefined`. `T` es el tipo del estado (puede incluir `undefined`
 * o `null` si no hay valor inicial); `S` es lo que se emite al cambiar (por defecto, `T`).
 */
export function useControllable(value, defaultValue, onChange) {
    const [inner, setInner] = useState(defaultValue);
    const controlled = value !== undefined;
    const current = controlled ? value : inner;
    const set = useCallback((next) => {
        if (!controlled)
            setInner(next);
        onChange?.(next);
    }, [controlled, onChange]);
    return [current, set];
}
/** `true` si el sistema pide reducir movimiento (escucha cambios en vivo). */
export function useReducedMotion() {
    const query = '(prefers-reduced-motion: reduce)';
    const [reduced, setReduced] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches);
    useEffect(() => {
        const m = window.matchMedia?.(query);
        if (!m)
            return undefined;
        const on = () => setReduced(m.matches);
        m.addEventListener?.('change', on);
        return () => m.removeEventListener?.('change', on);
    }, []);
    return reduced;
}
/* ── Movimiento ─────────────────────────────────────────────────────────── */
const D = { fast: 'var(--db-dur-fast)', base: 'var(--db-dur-base)', close: 'var(--db-dur-close)', open: 'var(--db-dur-open)' };
const E = { spring: 'var(--db-ease-spring)', out: 'var(--db-ease-out)', expo: 'var(--db-ease-expo)' };
/** [transform dur, transform ease, opacity dur] al abrir y al cerrar. Resorte al abrir, suave al cerrar. */
const PRESETS = {
    scrim: { from: null, open: [D.base, E.out, D.base], close: [D.base, E.out, D.base] },
    dialog: { from: 'translate3d(0,18px,0) scale(.96)', open: [D.open, E.spring, D.base], close: [D.close, E.out, D.base] },
    palette: { from: 'translate3d(0,-10px,0) scale(.97)', open: [D.open, E.spring, D.base], close: [D.base, E.out, D.fast] },
    'drawer-right': { from: 'translate3d(calc(100% + 32px),0,0)', open: [D.open, E.spring, D.fast], close: [D.close, E.out, D.close] },
    'drawer-left': { from: 'translate3d(calc(-100% - 32px),0,0)', open: [D.open, E.spring, D.fast], close: [D.close, E.out, D.close] },
    sheet: { from: 'translate3d(0,100%,0)', open: [D.open, E.spring, D.fast], close: [D.close, E.out, D.close] },
    pop: { from: 'translate3d(0,-4px,0) scale(.96)', open: [D.base, E.spring, D.fast], close: [D.fast, E.out, D.fast] },
    tooltip: { from: 'scale(.94)', open: [D.base, E.spring, D.fast], close: [D.fast, E.out, D.fast] },
};
/** Milisegundos hasta desmontar al cerrar (coinciden con los tokens). */
export const EXIT_MS = { scrim: 350, dialog: 350, palette: 200, 'drawer-right': 620, 'drawer-left': 620, sheet: 620, pop: 140, tooltip: 140 };
/** Estilo inline de entrada/salida. Solo transform y opacity; con reduced-motion solo opacity. */
export function motionStyle(kind, visible, reduced, origin) {
    const p = PRESETS[kind] ?? PRESETS.pop;
    if (reduced)
        return { opacity: visible ? 1 : 0, transition: `opacity ${D.fast} linear` };
    const [td, te, od] = visible ? p.open : p.close;
    return {
        opacity: visible ? 1 : 0,
        transform: visible || !p.from ? undefined : p.from,
        transformOrigin: origin,
        transition: p.from ? `transform ${td} ${te}, opacity ${od} ${E.out}` : `opacity ${od} ${E.out}`,
        willChange: 'transform, opacity',
    };
}
/** Monta al abrir y desmonta tras la animación de salida. `visible` dispara la transición. */
export function usePresence(open, exitMs = 350) {
    const [mounted, setMounted] = useState(open);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        if (open) {
            setMounted(true);
            let r2 = 0;
            const r1 = requestAnimationFrame(() => {
                r2 = requestAnimationFrame(() => setVisible(true));
            });
            return () => {
                cancelAnimationFrame(r1);
                cancelAnimationFrame(r2);
            };
        }
        setVisible(false);
        const t = setTimeout(() => setMounted(false), exitMs);
        return () => clearTimeout(t);
    }, [open, exitMs]);
    return { mounted: mounted || open, visible: visible && open };
}
/* ── Pila de capas: Esc y clic fuera solo afectan a la capa superior ───── */
const stack = [];
export function useDismiss({ open, onClose, refs = [], escape = true, outside = true }) {
    // Con outside:false solo se emite 'escape', así que las dos firmas de onClose son seguras aquí.
    const cb = useRef(onClose);
    cb.current = onClose;
    const refsRef = useRef(refs);
    refsRef.current = refs;
    const portalTarget = useContext(PortalTargetContext);
    const enabled = !!open && !!onClose;
    useEffect(() => {
        if (!enabled)
            return undefined;
        const token = {};
        stack.push(token);
        const isTop = () => stack[stack.length - 1] === token;
        // Dentro de un Shadow DOM (mountInShadow) también se escucha en el shadow root: la extensión
        // puede detener los eventos en su borde y, desde `document`, el target sería el host.
        const roots = eventRoots([portalTarget, ...refsRef.current.map((r) => r?.current)]);
        const seen = new WeakSet(); // un evento que sube del shadow root al documento se atiende una vez
        const onKey = (e) => {
            if (seen.has(e))
                return;
            seen.add(e);
            if (escape && e.key === 'Escape' && isTop()) {
                e.preventDefault();
                cb.current?.('escape');
            }
        };
        const onDown = (e) => {
            if (seen.has(e))
                return;
            seen.add(e);
            if (!outside || !isTop())
                return;
            const path = e.composedPath?.() ?? [];
            const t = e.target;
            if (refsRef.current.some((r) => r?.current && (path.includes(r.current) || r.current.contains(t))))
                return;
            cb.current?.('outside');
        };
        roots.forEach((root) => {
            root.addEventListener('keydown', onKey);
            root.addEventListener('pointerdown', onDown);
        });
        return () => {
            const i = stack.indexOf(token);
            if (i >= 0)
                stack.splice(i, 1);
            roots.forEach((root) => {
                root.removeEventListener('keydown', onKey);
                root.removeEventListener('pointerdown', onDown);
            });
        };
    }, [enabled, escape, outside, portalTarget]);
}
/**
 * Atrapa el foco dentro de `containerRef` mientras `active`.
 * Al activarse enfoca `initialFocusRef` o el primer enfocable (sin desplazar la página);
 * al desactivarse devuelve el foco a quien lo tenía. Devuelve el onKeyDown para Tab.
 */
export function useFocusTrap(containerRef, { active, autoFocus = true, initialFocusRef, restoreFocus = true } = {}) {
    useEffect(() => {
        if (!active || !autoFocus)
            return undefined;
        const prev = getActiveElement(containerRef.current);
        const id = requestAnimationFrame(() => {
            const root = containerRef.current;
            const el = initialFocusRef?.current || getFocusable(root)[0] || root;
            el?.focus?.({ preventScroll: true });
        });
        return () => {
            cancelAnimationFrame(id);
            if (restoreFocus && prev && typeof prev.focus === 'function' && prev.isConnected)
                prev.focus({ preventScroll: true });
        };
    }, [active, autoFocus]); // eslint-disable-line react-hooks/exhaustive-deps
    return useCallback((e) => {
        if (e.key !== 'Tab')
            return;
        const items = getFocusable(containerRef.current);
        if (!items.length) {
            e.preventDefault();
            return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        const active = getActiveElement(containerRef.current);
        if (e.shiftKey && (active === first || active === containerRef.current)) {
            e.preventDefault();
            last.focus();
        }
        else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
        }
    }, [containerRef]);
}
/**
 * Vuelve `inert` todo lo que no sea la capa mientras está abierta.
 * mode "portal": hermanos en <body> · mode "inline": hermanos dentro del contenedor.
 */
export function useInertOthers(ref, active, mode = 'portal') {
    const portalTarget = useContext(PortalTargetContext);
    useEffect(() => {
        if (!active)
            return undefined;
        const node = ref.current;
        if (!node)
            return undefined;
        const parent = mode === 'portal' ? portalTarget ?? document.body : node.parentElement;
        if (!parent)
            return undefined;
        const changed = [];
        for (const el of [...parent.children]) {
            if (el === node || el.contains(node) || el.hasAttribute('inert') || el.tagName === 'SCRIPT' || el.hasAttribute('data-db-live'))
                continue;
            el.setAttribute('inert', '');
            changed.push(el);
        }
        return () => changed.forEach((el) => el.removeAttribute('inert'));
    }, [active, mode, ref, portalTarget]);
}
let locks = 0;
let prevOverflow = '';
/** Bloquea el scroll del documento (solo capas modales en portal). */
export function useScrollLock(active) {
    useEffect(() => {
        if (!active)
            return undefined;
        const html = document.documentElement;
        if (locks++ === 0) {
            prevOverflow = html.style.overflow;
            html.style.overflow = 'hidden';
        }
        return () => {
            if (--locks === 0)
                html.style.overflow = prevOverflow;
        };
    }, [active]);
}
/** Atajo de teclado. combo: "mod+k" (Ctrl en Windows/Linux, ⌘ en Mac). scopeRef limita dónde escucha. */
export function useHotkey(combo, handler, { enabled = true, scopeRef } = {}) {
    const cb = useRef(handler);
    cb.current = handler;
    useEffect(() => {
        if (!enabled)
            return undefined;
        const parts = combo.toLowerCase().split('+');
        const key = parts[parts.length - 1];
        const mod = parts.includes('mod');
        const shift = parts.includes('shift');
        const onKey = (e) => {
            if (e.key?.toLowerCase() !== key)
                return;
            if (mod && !(e.ctrlKey || e.metaKey))
                return;
            if (!mod && (e.ctrlKey || e.metaKey))
                return;
            if (shift !== e.shiftKey)
                return;
            if (scopeRef && !(scopeRef.current && scopeRef.current.contains(e.target)))
                return;
            e.preventDefault();
            e.stopPropagation();
            cb.current(e);
        };
        if (scopeRef) {
            const target = scopeRef.current;
            target?.addEventListener('keydown', onKey);
            return () => target?.removeEventListener('keydown', onKey);
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [combo, enabled, scopeRef]);
}
/** ¿Mac? Para mostrar ⌘ en lugar de Ctrl. */
export function isMac() {
    return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');
}
/* ── Destino de los portales ──────────────────────────────────────────────
 * Por defecto, modales, menús, popovers y tooltips se montan en <body>. Dentro de un
 * Shadow DOM (UI inyectada por una extensión en páginas de terceros) eso los sacaría del
 * árbol aislado y quedarían sin estilos: envuelve la app con <DbPortalTarget container={…}>
 * apuntando a un nodo DENTRO del shadow root (lo hace mountInShadow). */
const PortalTargetContext = createContext(null);
/** Define dónde se montan los portales del sistema dentro de este subárbol. */
export function DbPortalTarget({ container, children }) {
    return <PortalTargetContext.Provider value={container}>{children}</PortalTargetContext.Provider>;
}
/** Nodo destino actual de los portales (null = <body>). */
export function usePortalTarget() {
    return useContext(PortalTargetContext);
}
/**
 * Portal a <body> (o al destino de DbPortalTarget) que conserva el tema del contexto
 * (data-theme) para que la capa herede tokens aunque se monte fuera del contenedor.
 * `disabled` = render en línea.
 */
export function Portal({ children, disabled = false }) {
    const { theme } = useDbTheme();
    const target = useContext(PortalTargetContext);
    // En el navegador el portal se monta en el PRIMER render: si esperara a un efecto, el
    // FloatingLayer mediría un ref nulo y la capa se quedaría oculta (visibility: hidden).
    // Solo en servidor (sin document) espera al efecto.
    const [ready, setReady] = useState(() => typeof document !== 'undefined');
    useEffect(() => {
        if (!ready)
            setReady(true);
    }, [ready]);
    if (disabled)
        return children;
    if (!ready || typeof document === 'undefined')
        return null;
    return createPortal(<div data-theme={theme} data-db-portal="" className="font-sans text-ink">
      {children}
    </div>, target ?? document.body);
}
const INLINE_POS = {
    'bottom-start': 'left-0 top-full',
    'bottom-end': 'right-0 top-full',
    bottom: 'left-1/2 top-full -translate-x-1/2',
    'top-start': 'left-0 bottom-full',
    'top-end': 'right-0 bottom-full',
    top: 'left-1/2 bottom-full -translate-x-1/2',
    'right-start': 'left-full top-0',
    'right-end': 'left-full bottom-0',
    right: 'left-full top-1/2 -translate-y-1/2',
    'left-start': 'right-full top-0',
    'left-end': 'right-full bottom-0',
    left: 'right-full top-1/2 -translate-y-1/2',
};
/** transform-origin según el lado y la alineación (la capa crece desde su ancla). */
export function originFor(placement = 'bottom-start') {
    const [side, align] = placement.split('-');
    const cross = align === 'start' ? 'left' : align === 'end' ? 'right' : 'center';
    const crossV = align === 'start' ? 'top' : align === 'end' ? 'bottom' : 'center';
    if (side === 'bottom')
        return `${cross} top`;
    if (side === 'top')
        return `${cross} bottom`;
    if (side === 'right')
        return `left ${crossV}`;
    return `right ${crossV}`;
}
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
/**
 * FloatingLayer — coloca una capa junto a su ancla.
 * inline: `absolute` dentro del contenedor `relative` del disparador (sin medir; funciona escalado).
 * portal (por defecto): `fixed` en <body>, con giro (flip) y ajuste al viewport.
 * point: {x,y} para menús contextuales (relativo al contenedor en inline; al viewport en portal).
 * children puede ser función ({ placement }) => nodo, con el lado final tras el flip.
 */
export const FloatingLayer = forwardRef(function FloatingLayer({ anchorRef, point, inline = false, placement = 'bottom-start', offset = 8, matchWidth = false, layer = 'overlay', className, style, children, ...rest }, ref) {
    const local = useRef(null);
    const [pos, setPos] = useState(null);
    const px = point?.x;
    const py = point?.y;
    useLayoutEffect(() => {
        if (inline)
            return undefined;
        const el = local.current;
        if (!el)
            return undefined;
        const update = () => {
            const anchor = anchorRef?.current;
            // Con `point`, x e y siempre vienen juntos.
            const a = px != null
                ? { left: px, right: px, top: py, bottom: py, width: 0, height: 0 }
                : anchor?.getBoundingClientRect();
            if (!a)
                return;
            const fw = el.offsetWidth;
            const fh = el.offsetHeight;
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const m = 8;
            let [side, align] = placement.split('-');
            if (side === 'bottom' && a.bottom + offset + fh > vh - m && a.top - offset - fh >= m)
                side = 'top';
            else if (side === 'top' && a.top - offset - fh < m && a.bottom + offset + fh <= vh - m)
                side = 'bottom';
            else if (side === 'right' && a.right + offset + fw > vw - m && a.left - offset - fw >= m)
                side = 'left';
            else if (side === 'left' && a.left - offset - fw < m && a.right + offset + fw <= vw - m)
                side = 'right';
            let top;
            let left;
            if (side === 'bottom' || side === 'top') {
                top = side === 'bottom' ? a.bottom + offset : a.top - offset - fh;
                left = align === 'start' ? a.left : align === 'end' ? a.right - fw : a.left + a.width / 2 - fw / 2;
            }
            else {
                left = side === 'right' ? a.right + offset : a.left - offset - fw;
                top = align === 'start' ? a.top : align === 'end' ? a.bottom - fh : a.top + a.height / 2 - fh / 2;
            }
            setPos({
                top: clamp(top, m, Math.max(m, vh - fh - m)),
                left: clamp(left, m, Math.max(m, vw - fw - m)),
                width: a.width,
                placement: (align ? `${side}-${align}` : side),
            });
        };
        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
        ro?.observe(el);
        if (anchorRef?.current)
            ro?.observe(anchorRef.current);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
            ro?.disconnect();
        };
    }, [inline, placement, offset, px, py, anchorRef]);
    const finalPlacement = inline ? placement : pos?.placement ?? placement;
    const body = typeof children === 'function' ? children({ placement: finalPlacement }) : children;
    const zClass = layer === 'tooltip' ? 'z-tooltip' : layer === 'modal' ? 'z-modal' : 'z-overlay';
    if (inline) {
        const side = placement.split('-')[0];
        const gap = { bottom: { marginTop: offset }, top: { marginBottom: offset }, right: { marginLeft: offset }, left: { marginRight: offset } }[side];
        return (<div ref={mergeRefs(ref, local)} className={cn('absolute', zClass, px == null && INLINE_POS[placement], matchWidth && 'min-w-full', className)} style={{ ...(px != null ? { left: px, top: py } : gap), ...style }} {...rest}>
        {body}
      </div>);
    }
    return (<Portal>
      <div ref={mergeRefs(ref, local)} 
    // w-max: el ancho sale del contenido (no del viewport). Sin esto, un hijo con w-full en un
    // contenedor fixed ocupa toda la pantalla y la capa se mide y se coloca mal.
    className={cn('fixed w-max', zClass, className)} style={{
            top: pos?.top ?? 0,
            left: pos?.left ?? 0,
            // matchWidth: mismo ancho que el ancla (equivale a w-full del modo en línea; el estilo gana a
            // clases como w-full, que en un contenedor fixed medirían el viewport).
            ...(matchWidth ? { width: pos?.width ?? anchorRef?.current?.getBoundingClientRect().width } : null),
            maxWidth: 'calc(100vw - 16px)',
            visibility: pos ? 'visible' : 'hidden',
            ...style,
        }} {...rest}>
        {body}
      </div>
    </Portal>);
});
